import { z } from 'zod';
import { SortFieldSchema } from './general';
// For types only, use type import
import type { CoMarketingCampaignLinkSchema, CoMarketingCampaignLinkSchema as CoMarketingCampaignLinkSchemaType } from './coMarketing';
import { CoMarketingCampaignLink } from '@/types';

export const CampaignTypeSchema = z.string();

export const ProductFeedTypeSchema = z.enum(['mostPurchased', 'abandoned', 'recommended', 'all']);

export const DynamicContentFilterSchema = z.object({
	brands: z.array(z.string()).optional(),
	contentType: ProductFeedTypeSchema,
	store: z.array(z.number()).optional(),
	categories: z.array(z.string()).optional(),
	quantityMin: z.number().optional(),
	blockCount: z.number().optional(),
	brandPerc: z.number().optional(),
	priceMin: z.number().optional(),
	priceMax: z.number().optional(),
});

export const MediaObjectSchema = z.object({
	url: z.string(),
	contentType: z.string(),
});

export const CampaignSendWindowSchema = z.object({
	start: z.number(),
	end: z.number(),
});

export const MessagingReportSchema = z.object({
	emails: z.array(z.string()),
	start: z.number(),
	end: z.number(),
	optIn: z.boolean(),
	optOut: z.boolean(),
});

export const ChangesSchema = z.object({
	uid: z.string(),
	type: z.string(),
	previousValue: z.any(),
	currentValue: z.any(),
	updated: z.number().optional(),
});

export const TriggersSchema = z.object({
	abandonedCart: z.boolean().optional(),
	postSale: z.boolean().optional(),
	birthday: z.boolean().optional(),
	geoFence: z.boolean().optional(),
});

export const RecipeInfoSchema = z.object({
	objectID: z.string(),
	id: z.string(),
	username: z.string(),
	userID: z.string(),
	name: z.string(),
	newID: z.string(),
	brandLiability: z.number(),
	budget: z.number(),
	startDate: z.number().optional(),
	endDate: z.number().optional(),
	haltDate: z.number().optional(),
	hasBudget: z.boolean(),
	allowOverrun: z.boolean(),
	approved: z.boolean(),
	ignore: z.boolean(),
	admin: z.boolean(),
	noApproval: z.boolean(),
});

export const DateRangeSchema = z.object({
	start: z.number().optional(),
	end: z.number().optional(),
	active: z.boolean().optional(),
});

export const CampaignScheduleSchema = z.object({
	dateRange: DateRangeSchema.optional(),
	scheduled: z.number(),
	recurDays: z.number(),
	blast: z.boolean(),
	sendWindow: CampaignSendWindowSchema.optional(),
});

export const CampaignTargetingSchema = z.object({
	surveys: z.array(z.string()),
	signupForms: z.array(z.string()),
	audiences: z.array(z.string()),
	exclude: z.array(z.string()),
	storeIDs: z.array(z.number()),
});

export const CampaignMessageCapsSchema = z.object({
	capPerDay: z.number(),
	lifetimeCap: z.number(),
	sendAtTimePredicted: z.boolean(),
});

export const ChannelConfigSchema = z.object({
	schedule: CampaignScheduleSchema.optional(),
	targeting: CampaignTargetingSchema.optional(),
	messageCaps: CampaignMessageCapsSchema.optional(),
});

export const CampaignChannelTypeSchema = z.enum([
	'emailTemplate',
	'browserTemplate',
	'pushTemplate',
	'voiceTemplate',
	'smsTemplate',
	'adTemplate',
	'snailMailTemplate',
]);

export const EmailTemplateSchema = z
	.object({
		dynamicContent: DynamicContentFilterSchema.optional(),
		slugs: z.array(z.string()).optional(),
		fromName: z.string().optional(),
		preheader: z.string().optional(),
		html: z.string().optional(),
		subject: z.string().optional(),
		from: z.string().optional(),
		json: z.any().optional(),
		priority: z.number().optional(),
		custom: z.boolean().optional(),
		inUse: z.boolean().optional(),
	})
	.extend(ChannelConfigSchema.shape);

export const BrowserTemplateSchema = z
	.object({
		title: z.string().optional(),
		body: z.string().optional(),
		link: z.string().optional(),
		mediaURL: z.string().optional(),
		mediaContentType: z.string().optional(),
		priority: z.number().optional(),
		inUse: z.boolean().optional(),
	})
	.extend(ChannelConfigSchema.shape);

export const PushTemplateSchema = z
	.object({
		title: z.string().optional(),
		body: z.string().optional(),
		mediaURL: z.string().optional(),
		mediaContentType: z.string().optional(),
		priority: z.number().optional(),
		inUse: z.boolean().optional(),
	})
	.extend(ChannelConfigSchema.shape);

export const VoiceTemplateSchema = z
	.object({
		ttv: z.string().optional(),
		lo: z.string().optional(),
		voiceFile: z.string().optional(),
		discountTemplate: z.string().optional(),
		priority: z.number().optional(),
		inUse: z.boolean().optional(),
	})
	.extend(ChannelConfigSchema.shape);

export const SnailMailTemplateSchema = z
	.object({
		front: z.string().optional(),
		back: z.string().optional(),
		size: z.string().optional(),
		priority: z.number().optional(),
		currentResident: z.boolean().optional(),
		inUse: z.boolean().optional(),
	})
	.extend(ChannelConfigSchema.shape);

export const LegacyOptionTypeSchema = z.enum(['regular', 'regulated', 'mms-only', 'sms-10dlc', 'mms-10dlc']);
export const StandardOptionTypeSchema = z.enum(['regular', 'withLandingPage', 'mmsImageOnly']);
export const Restricted10dlcOptionTypeSchema = z.enum(['smsWithLandingPage', 'mmsWithLandingPage']);

export const OptionTypeSchema = z.union([LegacyOptionTypeSchema, StandardOptionTypeSchema, Restricted10dlcOptionTypeSchema]);

export const SMSTemplateSchema = z
	.object({
		dynamicContent: DynamicContentFilterSchema.optional(),
		slugs: z.array(z.string()).optional(),
		body: z.string().optional(),
		timezone: z.string().optional(),
		mediaURL: z.string().optional(),
		mediaContentType: z.string().optional(),
		mmsMediaURL: z.string().optional(),
		mmsMediaContentType: z.string().optional(),
		msgContainer: z.string().optional(),
		priority: z.number().optional(),
		scheduled: z.number().optional(),
		recurDays: z.number().optional(),
		capPerDay: z.number().optional(),
		isMMS: z.boolean().optional(),
		mmsOnly: z.boolean().optional(),
		agnostic: z.boolean().optional(),
		landingHTML: z.string().optional(),
		landingDesign: z.any().optional(),
		landingType: z.string().optional(),
		optionType: OptionTypeSchema.optional(),
		inUse: z.boolean().optional(),
	})
	.extend(ChannelConfigSchema.shape);

export const AdTemplateSchema = z
	.object({
		thirdParty: z.boolean().optional(),
		ad: z.string().optional(),
		retailerTarget: z.record(z.array(z.number())).optional(),
		budget: z.number().optional(),
		adsPerCustomer: z.number().optional(),
		clickthrough: z.string().optional(),
		mediaURL: z.string().optional(),
		mediaContentType: z.string().optional(),
		media: z.record(MediaObjectSchema).optional(),
		learn: z.string().optional(),
		shop: z.string().optional(),
		largeBanner: z.boolean().optional(),
		showButtons: z.boolean().optional(),
		shopButtonText: z.string().optional(),
		shopButtonStyle: z.string().optional(),
		learnButtonText: z.string().optional(),
		learnButtonStyle: z.string().optional(),
		priority: z.number().optional(),
		inUse: z.boolean().optional(),
	})
	.extend(ChannelConfigSchema.shape);

export const ChannelTemplateSchema = z.object({
	emailTemplate: EmailTemplateSchema.optional(),
	browserTemplate: BrowserTemplateSchema.optional(),
	pushTemplate: PushTemplateSchema.optional(),
	voiceTemplate: VoiceTemplateSchema.optional(),
	smsTemplate: SMSTemplateSchema.optional(),
	templates: SMSTemplateSchema.optional(),
	adTemplate: AdTemplateSchema.optional(),
	snailMailTemplate: SnailMailTemplateSchema.optional(),
	id: z.string(),
	userID: z.string(),
	name: z.string(),
	created: z.number(),
	updated: z.number(),
	archived: z.boolean(),
});

export const LiteTemplateSchema = z.object({
	landingType: z.string(),
	mediaURL: z.string(),
	mediaContentType: z.string(),
	mmsMediaURL: z.string(),
	mmsMediaContentType: z.string(),
	priority: z.number(),
	ad: z.string(),
	thirdParty: z.boolean(),
});

export const XLiteTemplateSchema = z.object({
	inUse: z.boolean(),
	priority: z.number(),
});

export const SendingTypeSchema = z.enum(['regular', 'variant', 'waterfall']);

export const ABVariantSchema = z.object({
	id: z.string(),
	emailTemplate: EmailTemplateSchema.optional(),
	templates: SMSTemplateSchema.optional(),
	// Percentage of targeted users that will receive this variant, maximum 1
	percentage: z.number(),
});

export const ABVariantLiteSchema = z.object({
	id: z.string(),
	emailTemplate: LiteTemplateSchema.optional(),
	templates: LiteTemplateSchema.optional(),
	// Percentage of targeted users that will receive this variant, maximum 1
	percentage: z.number(),
});

const CoMarketingCampaignLinkSchemaLazy: z.ZodType<CoMarketingCampaignLink> = z.lazy(() => {
	const { CoMarketingCampaignLinkSchema } = require('./coMarketing');
	return CoMarketingCampaignLinkSchema;
});

export const CampaignSchema = z
	.object({
		storeIDHash: z.record(z.array(z.number())),
		pushTemplate: PushTemplateSchema.optional(),
		emailTemplate: EmailTemplateSchema.optional(),
		templates: SMSTemplateSchema.optional(),
		adTemplate: AdTemplateSchema.optional(),
		dateRange: DateRangeSchema.optional(),
		browserTemplate: BrowserTemplateSchema.optional(),
		snailMailTemplate: SnailMailTemplateSchema.optional(),
		voiceTemplate: VoiceTemplateSchema.optional(),
		abVariants: z.array(ABVariantSchema).optional(),
		relations: z.record(z.string()),
		id: z.string(),
		sendingType: SendingTypeSchema,
		agencyID: z.string(),
		name: z.string(),
		defaultGroupID: z.string(),
		billingStoreID: z.string(),
		clonedFrom: z.string(),
		avatar: z.string(),
		userID: z.string(),
		type: CampaignTypeSchema,
		timezone: z.string(),
		merpt: MessagingReportSchema.optional(),
		conversionActions: z.array(z.string()),
		tags: z.array(z.string()),
		surveyIDs: z.array(z.string()), // Survey ID's for targeting
		conversionWindow: z.number(),
		created: z.number().optional(),
		giftPoints: z.number(),
		approved: z.number(),
		updated: z.number().optional(),
		messageExpiryUnit: z.number(),
		recipeUserID: z.string(),
		recipe: RecipeInfoSchema,
		lockedReason: z.string(),
		budgetCaps: z.record(z.number()),
		recipes: z.record(z.boolean()),
		messageExpiry: z.number(),
		triggers: TriggersSchema,
		emailOptinCampaign: z.boolean(),
		optinCampaign: z.boolean(),
		enableQueue: z.boolean(),
		default: z.boolean(),
		isLocked: z.boolean(),
		draft: z.boolean(),
		isActive: z.boolean(),
		archived: z.boolean(),
		isCoMarketing: z.boolean(),
		coMarketing: CoMarketingCampaignLinkSchemaLazy.optional(),
	})
	.extend(CampaignScheduleSchema.shape)
	.extend(CampaignTargetingSchema.shape)
	.extend(CampaignMessageCapsSchema.shape);

export const CampaignChangeSetSchema = z.object({
	changes: z.record(ChangesSchema),
	diffRetailer: z.lazy((): z.ZodType<any> => CampaignSchema).optional(),
	diffBrand: z.lazy((): z.ZodType<any> => CampaignSchema).optional(),
	uid: z.string(),
	updated: z.number().optional(),
	approved: z.boolean(),
});

export const CampaignWithChangeSetSchema = CampaignSchema.extend({
	changeSet: CampaignChangeSetSchema,
});

export const CampaignSortFieldsSchema = z.object({
	id: SortFieldSchema.optional(),
	name: SortFieldSchema.optional(),
	created: SortFieldSchema.optional(),
	updated: SortFieldSchema.optional(),
});

export const CampaignUIDSchema = z.object({
	snailMailTemplate: XLiteTemplateSchema.optional(),
	adTemplate: AdTemplateSchema.optional(),
	pushTemplate: XLiteTemplateSchema.optional(),
	voiceTemplate: XLiteTemplateSchema.optional(),
	emailTemplate: XLiteTemplateSchema.optional(),
	templates: XLiteTemplateSchema.optional(),
	dateRange: DateRangeSchema.optional(),
	relations: z.record(z.string()),
	browserTemplate: XLiteTemplateSchema.optional(),
	name: z.string(),
	userID: z.string(),
	id: z.string(),
	agencyID: z.string(),
	tags: z.array(z.string()),
	recipe: RecipeInfoSchema.optional(),
	recipes: z.record(z.boolean()).optional(),
	messageExpiry: z.number(),
	updated: z.number(),
	recurDays: z.number(),
	scheduled: z.number(),
	messageExpiryUnit: z.number(),
	created: z.number(),
	triggers: TriggersSchema,
	isActive: z.boolean(),
	archived: z.boolean(),
	isLocked: z.boolean().optional(),
	lockedReason: z.string().optional(),
	draft: z.boolean(),
	blast: z.boolean(),
	lastMessage: z.number().optional(),
	isCoMarketing: z.boolean(),
	coMarketing: CoMarketingCampaignLinkSchemaLazy.optional(),
});

export const VoiceDropLanguageSchema = z.object({
	key: z.string(),
	lngc: z.string(),
	g: z.string(),
	cn: z.string(),
	lngn: z.string(),
});

export const StaticProductSchema = z.object({
	imageUrl: z.string().optional(),
	productName: z.string().optional(),
	slug: z.string().optional(),
	salesPrice: z.number().optional(),
});

export const MessagePreviewSchema = z.object({
	body: z.string(),
	charset: z.string(),
	segments: z.number(),
});

export const FullMessagePreviewSchema = z.object({
	text: MessagePreviewSchema.optional(),
	email: MessagePreviewSchema.optional(),
	browser: MessagePreviewSchema.optional(),
	push: MessagePreviewSchema.optional(),
	ad: MessagePreviewSchema.optional(),
	snailMail: MessagePreviewSchema.optional(),
});

export const CampaignFiltersSchema = z.object({
	status: z.array(z.string()).optional(),
	types: z.array(z.string()).optional(),
	startDate: z.string().optional(),
	endDate: z.string().optional(),
	channels: z.array(z.string()).optional(),
	triggers: z.array(z.string()).optional(),
	tags: z.array(z.string()).optional(),
	pointsBoost: z.number().optional(),
	archived: z.number().optional(),
});

export const PaginationSchema = z.object({
	page: z.number(),
	limit: z.number(),
	totalResult: z.number().optional(),
});

export const FullCampaignQuerySchema = z.object({
	pagination: PaginationSchema,
	filters: CampaignFiltersSchema,
});

export const CampaignsQueryResponseSchema = z.object({
	campaigns: z.array(CampaignUIDSchema),
	pagination: PaginationSchema,
	filters: CampaignFiltersSchema,
});
